import { useAuth } from '@matix/auth';
import { PERM_NAME_TO_ID, type PosPermissionName } from '@matix/types';

/**
 * Permission gate hooks — BY NAME.
 *
 * Authorization is keyed on Security_lkp_Permissions.PermissionNameEn, which
 * is stable across tenant databases (numeric PermissionIDs are not). The JWT
 * carries `permissions.permission_names`; we match case-insensitively, exactly
 * like the legacy POS (UserHelper.IsAuthorized → .Trim().ToLower()).
 *
 * Usage:
 *   import { POS_PERMISSIONS } from '@matix/types';
 *   const canEdit = useCanI(POS_PERMISSIONS.EditOrder);
 *   {canEdit && <button onClick={handleEdit}>Edit</button>}
 *
 *   // multiple args = ANY (OR):
 *   const canEditAnything = useCanI(POS_PERMISSIONS.EditOrder, POS_PERMISSIONS.EditCartItemPrice);
 *
 * Back-compat: tokens issued before the name migration carry only numeric
 * `codes`. When `permission_names` is absent we fall back to matching the
 * canonical id of each requested name against `codes`.
 */

function buildHeld(
  names: string[] | undefined,
  codes: number[] | undefined,
): { nameSet: Set<string>; codeSet: Set<number> } {
  const nameSet = new Set<string>((names ?? []).map((n) => n.trim().toLowerCase()));
  const codeSet = new Set<number>(codes ?? []);
  return { nameSet, codeSet };
}

function holds(
  perm: PosPermissionName,
  nameSet: Set<string>,
  codeSet: Set<number>,
  hasNames: boolean,
): boolean {
  if (hasNames) return nameSet.has(perm.trim().toLowerCase());
  // Fallback: pre-migration token with only numeric codes.
  const id = PERM_NAME_TO_ID[perm.trim().toLowerCase()];
  return id !== undefined && codeSet.has(id);
}

/** True if the user holds AT LEAST ONE of the listed permissions (OR). */
export function useCanI(...permissions: PosPermissionName[]): boolean {
  const { cashier } = useAuth();
  const names = cashier?.permissions?.permission_names;
  const codes = cashier?.permissions?.codes;
  if (permissions.length === 0) return false;
  const hasNames = Array.isArray(names) && names.length > 0;
  if (!hasNames && (!codes || codes.length === 0)) return false;
  const { nameSet, codeSet } = buildHeld(names, codes);
  return permissions.some((p) => holds(p, nameSet, codeSet, hasNames));
}

/** True only if the user holds ALL of the listed permissions (AND). */
export function useCanIAll(...permissions: PosPermissionName[]): boolean {
  const { cashier } = useAuth();
  const names = cashier?.permissions?.permission_names;
  const codes = cashier?.permissions?.codes;
  if (permissions.length === 0) return false;
  const hasNames = Array.isArray(names) && names.length > 0;
  if (!hasNames && (!codes || codes.length === 0)) return false;
  const { nameSet, codeSet } = buildHeld(names, codes);
  return permissions.every((p) => holds(p, nameSet, codeSet, hasNames));
}

/**
 * Negative gate — legacy "Not Allow ..." permissions BLOCK an action when
 * held. Returns true when the user is BLOCKED from the given permission.
 * (Name-only; these have no canonical code.)
 */
export function useCannot(...permissions: PosPermissionName[]): boolean {
  const { cashier } = useAuth();
  const names = cashier?.permissions?.permission_names;
  if (permissions.length === 0) return false;
  if (!Array.isArray(names) || names.length === 0) return false;
  const nameSet = new Set(names.map((n) => n.trim().toLowerCase()));
  return permissions.some((p) => nameSet.has(p.trim().toLowerCase()));
}
